/**
 * `research.md` W-26: load a lazy chunk once the browser is idle, so the first use of a
 * screen or dialog does not wait on the network. Returns the cancel for an unmount.
 *
 * The idle callback alone is not a gate on a cold load. The main thread sits idle while the
 * network fetches the route's own chunks, so `requestIdleCallback` fired within milliseconds of
 * the shell mounting and these speculative graphs — the palette, the item detail, the reauth
 * dialog — raced the chunks the first screen was waiting for, over a connection already at its
 * limit (2026-09-21: they took 50 KB of the 200 KB/s the slow profile has, and the list's own
 * request waited). The load event is the gate: it fires when the document's own resources are
 * in, and only then is spare capacity spare. `document.readyState` covers the caller that
 * mounts after the event has already been dispatched.
 */
export function prefetchOnIdle(load: () => Promise<unknown>): () => void {
  const idle = typeof requestIdleCallback === "function" ? requestIdleCallback : (callback: () => void) => window.setTimeout(callback, 1500);
  const cancelIdle =
    typeof cancelIdleCallback === "function"
      ? cancelIdleCallback
      : (handle: number) => {
          window.clearTimeout(handle);
        };

  let cancelled = false;
  let handle: number | undefined;

  const schedule = () => {
    if (cancelled) return;
    handle = idle(() => {
      // A speculative fetch may fail offline or after unmount. The route's real load
      // owns the visible error; a background warm-up must not reject unhandled.
      void load().catch(() => undefined);
    });
  };

  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });

  return () => {
    cancelled = true;
    window.removeEventListener("load", schedule);
    if (handle !== undefined) cancelIdle(handle);
  };
}
